import { useRouter } from "next/router";
import { useEffect } from "react";
import { CategoryItemWrapper } from "../../../../services/context/CategoryItemContext";
import { useCartMutations } from "../../../../services/hooks/useCartMutations";
import { useAddToCartTracking } from "../../../../services/hooks/useAddToCartTracking";
import { useModalHook } from "../../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import { setGlobalSnackBarState } from "../../../../services/redux/slices/globalComponentsToggleSlice";
import { buildCategoryAddLineInput } from "../../../../utils/cart/cartLineTypes";
import {
  DISPLAYED_NAME,
  MENU_STYLE,
  PagesEnum,
  SNACK_BAR_ENUM,
} from "../../../../utils/enums";
import { getDisplayedName } from "../../../../utils/help/localization/localization";
import { getPageLink } from "../../../../utils/help/navigation/navigation";
import {
  CategoryType,
  ItemCategory,
  ItemType,
  Restaurant,
} from "../../../../utils/types";
import { SpeciallyForYouSection } from "../SpeciallyForYou/SpeciallyForYouSection";
import { CategoryItemCardV2 } from "./CategoryItem/CategoryItemCardNew/CategoryItemCardV2";
import { handleCartAnimation, handleScroll } from "./CategoryItemListAnimation";
import styles from "./CategoryListV2.module.scss";

export const CategoryListV2 = (props: {
  categories: CategoryType[] | null;
  logoUrl: string;
  disabledItemsIds: number[];
  flat?: boolean;
  restoreScrollPosition?: boolean;
}) => {
  const {
    categories,
    logoUrl,
    disabledItemsIds,
    flat,
    restoreScrollPosition = true,
  } = props;

  const menuView = useAppSelector(
    (state) => state.globalComponentsToggleSlice.menuView
  );

  const dispatch = useAppDispatch();
  const router = useRouter();

  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const restaurantConfig = restaurantInfo?.config ?? undefined;
  const { t } = useTranslationI18();
  const pricesExclVatText = t("common:prices-excl-vat");

  const isMobile = useAppSelector(
    (state) => state.globalComponentsToggleSlice.isMobile
  );
  const scrollYOfCategoryScreen = useAppSelector(
    (state) => state.globalComponentsToggleSlice.scrollYOfCategoryScreen
  );

  const { renderWebViewItem } = useModalHook();

  const handleScrollFunc = () => {
    handleScroll(categories);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScrollFunc, true);
    if (categories && categories.length > 0) {
      document
        .querySelector(`#navElm_${categories[0].id}`)
        ?.classList.add(`${styles.cat_button_select}`);
    }

    if (restoreScrollPosition) {
      setTimeout(() => {
        window.scrollTo({
          top: scrollYOfCategoryScreen,
          left: 0,
        });
      }, 100);
    }

    return () => {
      window.removeEventListener("scroll", handleScrollFunc, true);
    };
  }, []);

  const isListView = menuView !== MENU_STYLE.ITEM;

  if (!categories?.length) {
    return null;
  }

  return (
    <div onScroll={handleScrollFunc} className={styles.root}>
      <div className={styles.categories_column}>
        {/* A flat list is search results; a whole category of unmatched
            offers does not belong at the head of them. */}
        {!flat && <SpeciallyForYouSection isListView={isListView} />}
        {categories.map(
          (category) =>
            category?.itemCategories && (
              <CategorySection
                key={category.id}
                category={category}
                hideCategoryTitle={flat}
                disabledItemsIds={disabledItemsIds}
                logoUrl={logoUrl}
                isListView={isListView}
                dispatch={dispatch}
                router={router}
                isMobile={isMobile}
                renderWebViewItem={renderWebViewItem}
                t={t}
                pricesExclVatText={pricesExclVatText}
                restaurantConfig={restaurantConfig}
              />
            )
        )}
      </div>
    </div>
  );
};

type CategorySectionProps = {
  category: CategoryType;
  disabledItemsIds: number[];
  logoUrl: string;
  isListView: boolean;
  dispatch: ReturnType<typeof useAppDispatch>;
  router: ReturnType<typeof useRouter>;
  isMobile: boolean;
  renderWebViewItem: (id: number) => void;
  t: (key: string) => string;
  pricesExclVatText: string;
  restaurantConfig: Restaurant["config"] | undefined;
  hideCategoryTitle?: boolean;
};

const CategorySection = (props: CategorySectionProps) => {
  const { hideCategoryTitle } = props;
  const {
    category,
    disabledItemsIds,
    logoUrl,
    isListView,
    dispatch,
    router,
    isMobile,
    renderWebViewItem,
    t,
    pricesExclVatText,
    restaurantConfig,
  } = props;

  const { addItem: addCartItem } = useCartMutations();
  const trackAddToCart = useAddToCartTracking();

  const availableItems = category.itemCategories.filter(
    (itemCategory) => itemCategory.item.timeBoundAvailabilityData.isAvailable
  );
  const unavailableItems = category.itemCategories.filter(
    (itemCategory) => !itemCategory.item.timeBoundAvailabilityData.isAvailable
  );

  const sortedItemCategoriesByTimeBound = [
    ...availableItems,
    ...unavailableItems,
  ];

  const addItem = (_e: unknown, index: number) => {
    const newItem: ItemType = {
      ...sortedItemCategoriesByTimeBound[index].item,
    };
    if (newItem.itemHasOptions) {
      if (isMobile) {
        return router.push(
          `${
            getPageLink(router, PagesEnum.Item, {
              as: newItem.id + "",
            }).as
          }`
        );
      } else {
        return renderWebViewItem(newItem.id);
      }
    }
    if (newItem.subItems?.length > 0) {
      const itemWithEmptyAddons = { ...newItem.subItems[0] };
      itemWithEmptyAddons.allAddonGroups?.forEach((addonGroup) => {
        addonGroup.addonGroupSubItem = [];
      });
      const itemData = buildCategoryAddLineInput(
        "",
        1,
        newItem.subItems[0].price,
        itemWithEmptyAddons,
        newItem
      );

      addCartItem(itemData);

      dispatch(
        setGlobalSnackBarState({
          isOpen: true,
          text: `${getDisplayedName(newItem, DISPLAYED_NAME.ITEM_NAME)!} ${t(
            SNACK_BAR_ENUM.ADD_ITEM_TO_CART
          )}`,
        })
      );
      handleCartAnimation();

      trackAddToCart({
        subItemId: itemData.subItemDetails.id || itemData.itemDetails.id,
        subItemName:
          itemData.subItemDetails.fullName || itemData.subItemDetails.name,
        quantity: itemData.count,
        totalPrice: itemData.totalPrice,
        itemId: itemData.itemDetails.id,
      });
    }
  };

  return (
    <div className={styles.category_section} id={`category_${category?.id}`}>
      {!hideCategoryTitle && (
        <h4 className={styles.category_title}>
          {getDisplayedName(category, DISPLAYED_NAME.CATEGORY_NAME)}
        </h4>
      )}
      {!category.timeBoundAvailabilityData.isAvailable && (
        <span className={styles.category_time_bound}>
          {category.timeBoundAvailabilityData.fullMessage}
        </span>
      )}

      <div
        className={
          isListView ? styles.items_container_list : styles.items_container_grid
        }
        data-testid={
          isListView ? "items-container-list" : "items-container-grid"
        }
      >
        {sortedItemCategoriesByTimeBound.map(
          (itemCat: ItemCategory, index) =>
            disabledItemsIds &&
            !disabledItemsIds.includes(itemCat.item.id) && (
              <div
                key={itemCat.item.id}
                className={isListView ? styles.list_item_wrapper : undefined}
              >
                <CategoryItemWrapper
                  item={itemCat.item}
                  index={index}
                  categoryId={category?.id as unknown as number}
                  addItem={addItem}
                  logoUrl={logoUrl}
                  isMenuView={!isListView}
                >
                  <CategoryItemCardV2 />
                </CategoryItemWrapper>
              </div>
            )
        )}
      </div>

      {restaurantConfig?.pricesExclVatGuestLine && (
        <div className={styles.prices_excl_vat}>
          <p>{pricesExclVatText}</p>
        </div>
      )}
    </div>
  );
};
